import { type UnknownAction } from 'redux';
import { createAction } from 'typesafe-actions';

import { type FactorType, type ProtectedUrlMethods } from '../../models';

interface VerificationRequiredPayload {
  onSuccessAction?: UnknownAction;
  url: string;
  method: ProtectedUrlMethods;
}

/** Matches absolute URLs and scheme-relative URLs. */
const ABS_OR_SCHEME_REL = /^(https?:)?\/\//;

// Public actions
export default {
  /** Triggers the verification modal if MFA is enabled.
   * @param {UnknownAction} onSuccessAction redux message dispatched on successful verification
   */
  verificationRequired: createAction(
    'mfaPrompt/verificationRequired',
    ({
      url,
      method,
      onSuccessAction,
    }: VerificationRequiredPayload): VerificationRequiredPayload => {
      const rootRelativeUrl = ABS_OR_SCHEME_REL.test(url)
        ? `/${url.split('/').slice(3).join('/')}`
        : url;

      return { method, onSuccessAction, url: rootRelativeUrl };
    }
  )(),
};

// Private actions

/** Indicates the user has dismissed the verification prompt */
export const verificationDismissed = createAction(
  'mfaPrompt/verificationDismissed'
)<void>();

/** Indicates the user has successfully verified their identity */
export const verificationSucceeded = createAction(
  'mfaPrompt/verificationSucceeded'
)<void>();

/** Indicates the user has requested the available MFA factors */
export const availableFactorsRequested = createAction(
  'mfaPrompt/availableFactorsRequested'
)<void>();

/** Indicates the available MFA factors have been retrieved */
export const availableFactorsReceived = createAction(
  'mfaPrompt/availableFactorsReceived'
)<FactorType[]>();

/** Indicates an error was encountered while retrieving the available MFA factors */
export const availableFactorsNotReceived = createAction(
  'mfaPrompt/availableFactorsNotReceived'
)<void>();

export const protectedUrlsNotReceived = createAction(
  'mfaPrompt/protectedUrlsNotReceived'
)<void>();

export const protectedUrlsRequested = createAction(
  'mfaPrompt/protectedUrlsRequested'
)<void>();

export const protectedUrlsReceived = createAction('mfaPrompt/protectedUrls')<
  Record<ProtectedUrlMethods, string[]>
>();
